import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Html5Qrcode } from "html5-qrcode";
import {
    Camera, X, Check, Crown, Ban, ShieldAlert, ScanLine, ArrowLeft, Loader2, Printer, LogIn,
} from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { api, photoUrl } from "../lib/api";
import LanguageToggle from "../components/LanguageToggle";
import useKeepAlive from "../lib/useKeepAlive";
import KeepAliveBadge from "../components/KeepAliveBadge";

const QR_REGION_ID = "scottsclub-qr-region";

export default function Scan() {
    const { t } = useT();
    const navigate = useNavigate();
    const scannerRef = useRef(null);
    const [scanning, setScanning] = useState(false);
    const [busy, setBusy] = useState(false);
    const [member, setMember] = useState(null);
    const [manual, setManual] = useState("");
    const keepalive = useKeepAlive({ enabled: true });

    useEffect(() => {
        if (!localStorage.getItem("sc_admin_token")) {
            navigate("/admin/login", { replace: true });
        }
    }, [navigate]);

    const stopScanner = useCallback(async () => {
        try {
            if (scannerRef.current) {
                await scannerRef.current.stop();
                await scannerRef.current.clear();
            }
        } catch { /* ignore */ }
        scannerRef.current = null;
        setScanning(false);
    }, []);

    const verify = useCallback(async (payload) => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/scan", { payload });
            setMember(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("scan.error"));
        } finally {
            setBusy(false);
        }
    }, [t]);

    const startScanner = useCallback(async () => {
        setMember(null);
        setScanning(true);
        try {
            const instance = new Html5Qrcode(QR_REGION_ID, /* verbose */ false);
            scannerRef.current = instance;
            await instance.start(
                { facingMode: "environment" },
                {
                    // Faster scan: 25fps instead of 10, larger scanning region,
                    // and disableFlip=true skips redundant mirror decoding on each frame.
                    fps: 25,
                    qrbox: { width: 280, height: 280 },
                    aspectRatio: 1.0,
                    disableFlip: true,
                },
                async (decoded) => {
                    await stopScanner();
                    await verify(decoded);
                },
                () => { /* ignore per-frame errors */ }
            );
        } catch (e) {
            toast.error(e?.message || "Camera access denied.");
            setScanning(false);
        }
    }, [stopScanner, verify]);

    useEffect(() => () => { stopScanner(); }, [stopScanner]);

    const onManual = async (e) => {
        e.preventDefault();
        const val = manual.trim();
        if (!val) return;
        await verify(val);
        setManual("");
    };

    const checkIn = async () => {
        if (!member) return;
        try {
            const { data } = await api.post(`/admin/applications/${member.id}/check-in`);
            if (data?.duplicate) toast(t("occ.duplicate"));
            else if (data?.already_inside) toast(t("occ.already_inside"));
            else toast.success(t("scan.checked_in"));
            const { data: m } = await api.get(`/admin/applications/${member.id}`);
            setMember(m);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Check-in failed");
        }
    };

    const checkOut = async () => {
        if (!member) return;
        try {
            await api.post(`/admin/applications/${member.id}/check-out`);
            toast.success(t("occ.checked_out"));
            const { data: m } = await api.get(`/admin/applications/${member.id}`);
            setMember(m);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Check-out failed");
        }
    };

    const reset = async () => {
        setMember(null);
        await startScanner();
    };

    const isBlacklisted = member?.status === "blacklist";
    const isActive = member && (member.status === "approved" || member.status === "vip");

    return (
        <div className="min-h-[100dvh] text-ink-primary" data-testid="scan-page">
            {/* header */}
            <header className="sticky top-0 z-30 glass-strong border-b border-emerald-line/60">
                <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/admin" className="inline-flex items-center gap-2 text-ink-secondary hover:text-gold text-xs tracking-[0.22em] uppercase" data-testid="back-admin">
                        <ArrowLeft size={14} /> Admin
                    </Link>
                    <div className="flex items-center gap-3 font-serif text-lg">
                        <ScanLine size={18} className="text-gold" />
                        {t("scan.title")}
                    </div>
                    <div className="flex items-center gap-4">
                        <KeepAliveBadge online={keepalive.online} wakeLockActive={keepalive.wakeLockActive} />
                        <LanguageToggle />
                    </div>
                </div>
                <div className="gold-divider opacity-30" />
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
                <div>
                    <div className="label-eyebrow">{t("scan.eyebrow")}</div>
                    <h1 className="font-serif font-medium text-3xl md:text-4xl mt-3">{t("scan.title")}</h1>
                    <p className="text-ink-secondary mt-3 max-w-xl">{t("scan.subtitle")}</p>
                </div>

                {/* Scanner */}
                {!member && (
                    <div className="rounded-sm border border-emerald-line/60 bg-bg-secondary/60 p-4">
                        <div id={QR_REGION_ID} className="w-full max-w-md mx-auto rounded-sm overflow-hidden bg-black" style={{ minHeight: scanning ? 260 : 0 }} />
                        <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                            {!scanning ? (
                                <button
                                    onClick={startScanner}
                                    data-testid="start-scan"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright"
                                >
                                    <Camera size={16} /> {t("scan.start")}
                                </button>
                            ) : (
                                <button
                                    onClick={stopScanner}
                                    data-testid="stop-scan"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gold/40 text-gold rounded-sm hover:bg-gold/10"
                                >
                                    <X size={16} /> {t("scan.stop")}
                                </button>
                            )}
                        </div>

                        <form onSubmit={onManual} className="mt-8 border-t border-emerald-line/60 pt-6 flex flex-col sm:flex-row gap-3">
                            <input
                                value={manual}
                                onChange={(e) => setManual(e.target.value)}
                                placeholder={t("scan.manual")}
                                className="input-line flex-1"
                                data-testid="manual-input"
                            />
                            <button
                                type="submit"
                                disabled={busy || !manual.trim()}
                                data-testid="manual-verify"
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 disabled:opacity-40"
                            >
                                {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                {t("scan.verify")}
                            </button>
                        </form>
                    </div>
                )}

                {/* Result */}
                <AnimatePresence>
                    {member && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className={`relative rounded-sm border p-6 ${
                                isBlacklisted ? "border-red-700/60 bg-red-950/30" :
                                isActive ? "border-emerald-500/50 bg-emerald-950/20" :
                                "border-amber-500/50 bg-amber-950/20"
                            }`}
                            data-testid="scan-result"
                        >
                            <div className="flex items-start gap-4">
                                <div className="w-20 h-20 rounded-sm overflow-hidden bg-bg-surface border border-emerald-line/60 flex-shrink-0">
                                    {member.photo_id && (
                                        <img src={photoUrl(member.photo_id)} alt={member.full_name} className="w-full h-full object-cover" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <div className="font-serif text-2xl text-ink-primary truncate">{member.full_name}</div>
                                        {member.vip && <Crown size={18} className="text-gold flex-shrink-0" />}
                                    </div>
                                    <div className="text-sm text-ink-secondary mt-1">@{member.instagram} · {member.phone}</div>
                                    <div className="text-xs text-ink-muted mt-1">Visits: {member.check_ins} · Status: {member.status.toUpperCase()}</div>
                                </div>
                            </div>

                            <div className="mt-6">
                                {isBlacklisted ? (
                                    <div className="flex items-center gap-3 text-red-300 font-medium">
                                        <Ban size={20} /> {t("scan.blacklist_warn")}
                                    </div>
                                ) : isActive ? (
                                    <div className="flex items-center gap-3 text-emerald-300 font-medium">
                                        <Check size={20} /> {t("scan.ok_admit")}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3 text-amber-300 font-medium">
                                        <ShieldAlert size={20} /> {t("scan.inactive_warn")} ({member.status})
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 flex flex-col sm:flex-row gap-3">
                                {isActive && (
                                    member.currently_inside ? (
                                        <button
                                            onClick={checkOut}
                                            data-testid="scan-check-out"
                                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 border border-emerald-400/60 text-emerald-200 rounded-sm hover:bg-emerald-400/10"
                                        >
                                            <LogIn size={16} className="rotate-180" /> {t("occ.check_out")}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={checkIn}
                                            data-testid="scan-check-in"
                                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright"
                                        >
                                            <Check size={16} /> {t("scan.check_in_cta")}
                                        </button>
                                    )
                                )}
                                <button
                                    onClick={reset}
                                    data-testid="scan-again"
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 border border-gold/40 text-gold rounded-sm hover:bg-gold/10"
                                >
                                    <Camera size={16} /> {t("scan.scan_again")}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="border-t border-emerald-line/60 pt-6">
                    <Link
                        to="/admin/posters"
                        className="inline-flex items-center gap-2 text-gold text-sm tracking-[0.22em] uppercase hover:text-gold-bright"
                        data-testid="posters-link"
                    >
                        <Printer size={14} /> QR posters
                    </Link>
                </div>
            </main>
        </div>
    );
}
